import type { ReportSeverity } from '../report';
import { makeTypeGuardByProperty } from '../types/makeTypeGuardByProperty';
import type { TableRequestBody, TableResponseBase } from './Table';

export type TableCellValueWithReport<TValue> = {
  data: TValue;
  /** -1 means result not available in database; -1 was historically chosen to order such rows first */
  severity: -1 | ReportSeverity;
};

export type TableWithReportsCellValue<TValue> =
  | TValue
  | TableCellValueWithReport<TValue>;

export const isTableCellValueWithReport = makeTypeGuardByProperty<
  TableWithReportsCellValue<unknown>,
  TableCellValueWithReport<unknown>
>('severity');

export const isPlainTableCellValue = <TValue>(
  x: TableWithReportsCellValue<TValue>,
): x is TValue => !isTableCellValueWithReport(x);

export type RowWithReports<TRow> = {
  [Key in keyof TRow]: TableWithReportsCellValue<TRow[Key]>;
};

export interface TableWithReportsRequestBody<TRow>
  extends TableRequestBody<TRow> {
  /**
   * Reports to load with the table
   *
   * - string: frontend endpoint
   * - string[]: vReport._key values
   * */
  reports?: string | string[];
}

/**
 * Since table request with reports may return any subset of fields wrapped in object
 * along with severity, we have split types for request and response
 * with and without reports to avoid lots of type checking on data fields
 * in table requests that don't work with reports.
 *
 * If you don't work with reports, use simpler TableRequestBody/TableResponseBody type pair.
 */
export interface TableWithReportsResponseBody<TRow> extends TableResponseBase {
  data: RowWithReports<TRow>[];
}
